/**
 * Sponsor data loader for conference and sponsor information
 * Reads the "Sponzori i konferencije" sheet from the Excel file
 */

import { YEAR_CONFIG } from '../../config/yearConfig';

/**
 * Build configured sponsor years as numbers.
 * @returns {number[]}
 */
export const getConfiguredSponsorYears = () => {
  const start = Number(YEAR_CONFIG.baseYear);
  const end = Number(YEAR_CONFIG.lastDataYear);

  if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) {
    return [];
  }

  const years = [];
  for (let year = start; year <= end; year++) {
    years.push(year);
  }
  return years;
};

/**
 * Transform raw sponsor data into structured format
 * @param {Array} rawData - Raw data from Excel with yearly columns
 * @returns {Array} Transformed data with yearlyData object
 */
const transformSponsorData = (rawData) => {
  return rawData.map(row => {
    const transformed = {
      customerName: row['Customer rename'] || row['Customer Name'] || row['Klijent'] || '',
      groupName: row['GroupName rename'] || row['Group Name'] || row['Grupa'] || '',
      conferenceName: row['ConferenceName'] || row['Conference Name'] || row['Konferencija'] || '',
      yearlyData: {}
    };

    // Define the years to process from config boundaries
    const years = getConfiguredSponsorYears();
    
    // Parse yearly columns: "2022 Rooms", "2022 RN", "2022 Revenue", etc.
    years.forEach(year => {
      const roomKey = `${year} Rooms`;
      const rnKey = `${year} RN`;
      const revenueKey = `${year} Revenue`;
      
      // Only create yearlyData entry if at least one value exists
      if (row[roomKey] !== undefined || row[rnKey] !== undefined || row[revenueKey] !== undefined) {
        transformed.yearlyData[year] = {
          rooms: parseFloat(row[roomKey]) || 0,
          rn: parseFloat(row[rnKey]) || 0,
          revenue: parseFloat(row[revenueKey]) || 0
        };
      }
    });

    return transformed;
  });
};

/**
 * Load and transform sponsor data from Excel file (second sheet)
 * @param {boolean} raw - If true, returns raw data; if false, returns transformed data
 * @returns {Promise<Array>} The sponsor data array
 */
export const loadSponsorData = async (raw = false) => {
  try {
    // Import ExcelJS for secure Excel parsing
    const ExcelJS = await import('exceljs');
    
    const res = await fetch('/protfolio.xlsx');
    if (!res.ok) throw new Error(res.statusText);
    
    const arrayBuffer = await res.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    
    // Log all available sheet names to help identify the correct one
    const sheetNames = workbook.worksheets.map(ws => ws.name);
    console.log('Available sheets in Excel file:', sheetNames);
    
    // Try to find the sheet with conference data by looking for sheets with "konferencije" or "Sponzori i konferencije"
    // or use the sheet name that contains yearly columns
    let worksheet = workbook.worksheets.find(ws => 
      ws.name.toLowerCase().includes('sponzori i konferencije') ||
      ws.name.toLowerCase().includes('konferencije')
    );
    
    // If not found by name, try to find by checking columns
    if (!worksheet) {
      for (const ws of workbook.worksheets) {
        const headerRow = ws.getRow(1);
        const headers = [];
        headerRow.eachCell({ includeEmpty: true }, (cell) => {
          headers.push(cell.value ? String(cell.value) : '');
        });
        
        // Look for sheets with yearly columns (2022 Room, 2023 Revenue, etc.)
        const hasYearlyColumns = headers.some(h => 
          typeof h === 'string' && /20(22|23|24|25|26|27)\s+(Room|RN|Revenue)/i.test(h)
        );
        if (hasYearlyColumns) {
          worksheet = ws;
          console.log('Found sheet with yearly columns:', ws.name);
          break;
        }
      }
    }
    
    if (!worksheet) {
      throw new Error('Could not find sponsor/conference sheet with yearly data. Available sheets: ' + sheetNames.join(', '));
    }
    
    // Convert to JSON format similar to xlsx library
    const rawData = [];
    const headers = [];
    
    // Get headers from first row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      headers[colNumber - 1] = cell.value ? String(cell.value) : '';
    });
    
    // Process data rows
    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header row
      
      const rowData = {};
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const header = headers[colNumber - 1];
        if (header) {
          // Convert cell value to string to preserve formatting
          rowData[header] = cell.value != null ? String(cell.value) : '';
        }
      });
      
      // Only add rows that have at least one non-empty value
      if (Object.values(rowData).some(val => val !== '')) {
        rawData.push(rowData);
      }
    });
    
    console.log('Sponsor data loaded successfully:', {
      sheetName: worksheet.name,
      rowCount: rawData.length,
      columns: rawData.length > 0 ? Object.keys(rawData[0]) : [],
      sampleRow: rawData.length > 0 ? rawData[0] : null
    });
    
    // Return raw data or transformed data based on parameter
    if (raw) {
      return rawData;
    }
    
    const transformedData = transformSponsorData(rawData);
    
    console.log('Sponsor data transformed:', {
      transformedCount: transformedData.length,
      sampleTransformed: transformedData.length > 0 ? transformedData[0] : null
    });
    
    return transformedData;
  } catch (error) {
    console.error('Error loading sponsor data from Excel file:', error);
    throw error;
  }
};
